import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue'
import { useRoute } from '@nuxtjs/composition-api'
import { useApolloClient } from '@vue/apollo-composable'
import {
  TemplateEngineContext,
  useGetSectionWithQuestionsQuery,
  useUpdateImpactMutation,
  useUpdateImpactLeanMutation,
  useUpdateAccountMutation,
  useUpsertPromptRunMutation,
  useUpsertGhgSavingPotentialMutation,
  useUpsertImpactSolutionMutation,
} from '~/types/types'
import { validateUrl } from '~/functions/helpers'
import { GHG_ESTIMATOR_CONCEPT_ID } from '~/constants/concepts'
import IxTextarea from '~/components/ix/Textarea.vue'
import IxTextField from '~/components/ix/TextField.vue'

/** Explicit registry so dynamic <component :is> resolves correctly (pnpm / Nuxt 2). */

export default /*#__PURE__*/_defineComponent({
  __name: 'SectionImpactDynamic',
  props: {
    sectionId: null,
    context: null,
    impactId: { default: undefined },
    impactSolutionId: { default: undefined },
    accountId: { default: undefined },
    impactData: { default: undefined },
    accountData: { default: undefined },
    impactGoalData: { default: undefined },
    impactSolutionData: { default: undefined },
    ghgSavingPotentialData: { default: undefined },
    ghgSavingPotentialId: { default: undefined },
    disabled: { type: Boolean, default: false },
    inputTypeForIxId: { default: () => ({}) },
    appendIconForIxId: { default: () => ({}) },
    readonlyInputByIxId: { default: () => ({}) },
    normalizeValueByIxId: { default: () => ({}) },
    validatorByIxId: { default: () => ({}) },
    excludeQuestionIxIds: { default: () => [] },
    onlyQuestionIxIds: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { sectionId: number, context: TemplateEngineContext, impactId: number, impactSolutionId: number, accountId: string, impactData: Record<string, any>, accountData: Record<string, any>, impactGoalData: Record<string, any>, impactSolutionData: Record<string, any>, ghgSavingPotentialData: Record<string, any>, ghgSavingPotentialId: number, disabled: boolean, inputTypeForIxId: Record<string, string>, appendIconForIxId: Record<string, string>, readonlyInputByIxId: Record<string, boolean>, normalizeValueByIxId: Record<string, 'int' | 'float' | 'string' | 'date'>, validatorByIxId: Record<string, ((val: any) => boolean | string) | undefined>, excludeQuestionIxIds: string[], onlyQuestionIxIds: string[] };

const COMPONENT_REGISTRY: Record<string, typeof IxTextarea> = {
  'ix-textarea': IxTextarea,
  'ix-survey-text-input': IxTextarea,
  'ix-text-field': IxTextField,
}

/** Maps question.ix_id to ImpactSolutionInput field when ix_id is not the write key. */
const IMPACT_SOLUTION_FIELD_BY_IX_ID: Record<string, string> = {
  impactSolutionTargetGroup: 'targetGroup',
  impactSolutionKind: 'kind',
}

const impactSolutionWriteKey = (ixId: string) => IMPACT_SOLUTION_FIELD_BY_IX_ID[ixId] ?? ixId

const route = useRoute()
const { client: apolloClient } = useApolloClient()



const { result: sectionResult, loading } = useGetSectionWithQuestionsQuery(
  computed(() => ({ id: props.sectionId })),
  computed(() => ({ enabled: !!props.sectionId })),
)
const section = computed(() => sectionResult.value?.getSection || null)

const { mutate: updateImpact } = useUpdateImpactMutation()
const { mutate: updateImpactLean } = useUpdateImpactLeanMutation()
const { mutate: updateAccount } = useUpdateAccountMutation()
const { mutate: upsertGhgSavingPotential } = useUpsertGhgSavingPotentialMutation()
const { mutate: upsertPromptRun } = useUpsertPromptRunMutation()
const { mutate: upsertImpactSolution } = useUpsertImpactSolutionMutation()

const resolveValueFromObject = (source: Record<string, any> | undefined, ixId: string) => {
  if (!source || !ixId) return undefined
  return source[ixId]
}

const getQuestionValue = (question: any) => {
  const ixId = question?.ixId
  const targetEntity = question?.targetEntity
  if (!ixId) return null

  if (targetEntity === 'account') {
    return resolveValueFromObject(props.accountData, ixId)
  }
  if (targetEntity === 'ghg_saving_potential') {
    return resolveValueFromObject(props.ghgSavingPotentialData, ixId)
  }
  if (targetEntity === 'impact_solution') {
    return resolveValueFromObject(props.impactSolutionData, impactSolutionWriteKey(ixId))
  }
  if (targetEntity === 'impact' && (ixId === 'startingYear' || ixId === 'ghgGrowthFactorStartingYear')) {
    const y = resolveValueFromObject(props.impactData, 'startingYear')
    if (y !== undefined && y !== null && y !== '') {
      return Number(y)
    }
    const fallback = props.impactGoalData?.ghgStartingYear
    if (fallback !== undefined && fallback !== null && fallback !== '') {
      return Number(fallback)
    }
    return null
  }
  return resolveValueFromObject(props.impactData, ixId)
}

const resolveComponentName = (question: any) => {
  const component = question?.component
  if (component && typeof component === 'string') {
    if (COMPONENT_REGISTRY[component]) {
      return COMPONENT_REGISTRY[component]
    }
    return component
  }
  return IxTextarea
}

const shouldRenderQuestion = (question: any) => {
  const ixId = question?.ixId
  if (ixId && props.excludeQuestionIxIds.includes(ixId)) {
    return false
  }
  if (props.onlyQuestionIxIds.length > 0) {
    return !!ixId && props.onlyQuestionIxIds.includes(ixId)
  }
  if (question?.ixId === 'useGhgSavingPotentialTool') {
    return props.impactData?.conceptId === GHG_ESTIMATOR_CONCEPT_ID
  }
  if (question?.ixId === 'durationOfImpact') {
    return props.ghgSavingPotentialData?.isRecurringImpact === true
  }
  if (question?.component === 'ix-impact-emission-sources-table') {
    return props.ghgSavingPotentialData?.isScenarioA !== false
  }
  return true
}

const isDisplayOnlyQuestion = (question: any) => {
  const component = question?.component
  return (
    component === 'ix-impact-solution-editor-intro'
    || component === 'ix-impact-ghg-growth-factor-intro'
    || component === 'ix-impact-ghg-recurring-impact-guidance'
    || component === 'ix-impact-intro-notes'
  )
}

/** Mutations handled inside the component; no SectionImpactDynamic write path. */
const isSelfContainedQuestion = (question: any) => {
  const targetEntity = question?.targetEntity
  return (
    targetEntity === 'impact_solution_context'
    || targetEntity === 'impact_submit_context'
    || targetEntity === 'metric_assignment_context'
    || targetEntity === 'ghg_saving_potential_context'
  )
}

/** Vuetify checkbox rendered in-template (not via dynamic component). */
const isVuetifyCheckboxQuestion = (question: any) =>
  question?.component === 'VCheckbox'
  && (question?.targetEntity === 'ghg_saving_potential' || question?.targetEntity === 'impact_solution')

const resolveComponentProps = (question: any) => {
  const componentKey = question?.component
  const ixId = question?.ixId
  if (componentKey === 'ix-text-field') {
    const validator = props.validatorByIxId[ixId] || (ixId === 'website' ? validateUrl : undefined)
    const resolvedType = props.inputTypeForIxId[ixId] || 'text'
    return {
      type: resolvedType,
      appendIcon: props.appendIconForIxId[ixId],
      validator,
    }
  }
  if (componentKey === 'ix-textarea' || componentKey === 'ix-survey-text-input') {
    return {
      validateOnBlur: true,
      showTranslateButton: true,
    }
  }
  if (componentKey === 'ix-menu-date-picker') {
    return {
      appendIcon: props.appendIconForIxId[ixId] || 'mdi-calendar',
    }
  }
  if (componentKey === 'ix-impact-concept-input') {
    return {
      impactData: props.impactData,
      impactGoalData: props.impactGoalData,
    }
  }
  if (componentKey === 'ix-impact-metric-assignments-table') {
    return {
      impactId: props.impactId,
      impactData: props.impactData,
    }
  }
  if (componentKey === 'ix-impact-solutions-list') {
    return {}
  }
  if (componentKey === 'ix-impact-solution-concept-input') {
    return {
      impactSolutionData: props.impactSolutionData,
    }
  }
  if (componentKey === 'impact-goal-edit-impact-solution-source-input') {
    return {
      impactSolution: props.impactSolutionData,
      readonly: props.disabled,
    }
  }
  if (componentKey === 'ix-impact-ghg-result-table') {
    return {
      impactData: props.impactData,
      impactGoalData: props.impactGoalData,
      impactSolutionData: props.impactSolutionData,
    }
  }
  if (componentKey === 'ix-impact-ghg-growth-factor-intro') {
    return {
      ghgSavingPotentialData: props.ghgSavingPotentialData,
    }
  }
  if (componentKey === 'ix-select-starting-year-with-warning') {
    return {
      ventureId: String(route.params.venture ?? ''),
      controlled: true,
      startingYearFromParent: getQuestionValue(question) as number | null | undefined,
    }
  }
  if (componentKey === 'impact-goal-edit-growth-factor-assignments') {
    return {
      impactData: props.impactData,
      impactGoalData: props.impactGoalData,
      impactSolutionData: props.impactSolutionData,
      actualOrTarget:
        ixId === 'ghgGrowthFactorTargetAssignments' ? 'target' : 'actual',
    }
  }
  if (componentKey === 'ix-impact-emission-sources-scenario') {
    return {
      ghgSavingPotentialId: props.ghgSavingPotentialId,
      ghgSavingPotentialData: props.ghgSavingPotentialData,
      impactSolutionData: props.impactSolutionData,
      disabled: props.disabled,
    }
  }
  if (componentKey === 'ix-impact-emission-sources-table') {
    return {
      ghgSavingPotentialId: props.ghgSavingPotentialId,
      ghgSavingPotentialData: props.ghgSavingPotentialData,
      impactSolutionData: props.impactSolutionData,
      disabled: props.disabled,
    }
  }
  return {}
}
const isReadonlyInput = (ixId: string) => !!props.readonlyInputByIxId[ixId]
const normalizeByRule = (ixId: string, raw: any) => {
  const rule = props.normalizeValueByIxId[ixId]
  if (rule === 'int') {
    const parsed = raw === '' || raw === null || raw === undefined ? null : parseInt(String(raw), 10)
    return Number.isNaN(parsed as number) ? null : parsed
  }
  if (rule === 'float') {
    const parsed = raw === '' || raw === null || raw === undefined ? null : parseFloat(String(raw))
    return Number.isNaN(parsed as number) ? null : parsed
  }
  if (rule === 'date') {
    return raw || null
  }
  if (rule === 'string') return raw ?? ''
  return raw
}

const handleUpdate = (question: any, val: any) => {
  if (props.disabled) return
  const ixId = question?.ixId
  const targetEntity = question?.targetEntity
  if (!ixId) return
  const normalized = normalizeByRule(ixId, val)

  if (targetEntity === 'account') {
    if (!props.accountId) return
    updateAccount({
      input: {
        id: props.accountId,
        [ixId]: normalized,
      },
    })
    return
  }
  if (targetEntity === 'ghg_saving_potential') {
    if (!props.ghgSavingPotentialId) return
    if (ixId === 'isRecurringImpact' && normalized === false) {
      upsertGhgSavingPotential({
        input: {
          id: props.ghgSavingPotentialId,
          isRecurringImpact: false,
          durationOfImpact: 1,
        },
      })
      return
    }
    upsertGhgSavingPotential({
      input: {
        id: props.ghgSavingPotentialId,
        [ixId]: normalized,
      },
    })
    return
  }
  if (targetEntity === 'metric_assignment_context') {
    return
  }
  if (targetEntity === 'impact_submit_context') {
    return
  }
  if (targetEntity === 'impact_solution_context') {
    return
  }
  if (targetEntity === 'ghg_saving_potential_context') {
    return
  }
  if (targetEntity === 'impact_solution') {
    const solution = props.impactSolutionData
    if (!solution?.id) return
    const field = impactSolutionWriteKey(ixId)
    upsertImpactSolution({
      input: {
        id: solution.id,
        impactId: solution.impactId ?? props.impactId,
        ventureId: String(route.params.venture ?? ''),
        [field]: normalized,
      },
    })
    return
  }

  if (!props.impactId) return
  if (
    ixId === 'sdgTargets'
    || ixId === 'irisCategories'
    || ixId === 'euTaxonomy'
  ) {
    updateImpactLean({
      id: props.impactId,
      input: {
        [ixId]: normalized,
      },
    })
    return
  }
  if (ixId === 'startingYear' || ixId === 'ghgGrowthFactorStartingYear') {
    void Promise.resolve(
      updateImpact({
        id: props.impactId,
        input: {
          startingYear: normalized,
        },
      }),
    ).then(() => {
      apolloClient.cache.evict({ fieldName: 'listMetricAssignments' })
      apolloClient.cache.gc()
    })
    return
  }
  updateImpact({
    id: props.impactId,
    input: {
      [ixId]: normalized,
    },
  })
}
const handleAiSuggestion = (newValue: string, promptRunId: string, question: any) => {
  upsertPromptRun({
    input: {
      id: promptRunId,
      promptAccepted: true,
      previousFieldValue: newValue,
    },
  })
  handleUpdate(question, newValue)
}
const handleCheckboxChange = (question: any, v: unknown) => {
  const current = getQuestionValue(question)
  const next = typeof v === 'boolean' ? v : !current
  handleUpdate(question, next)
}

return { __sfc: true,COMPONENT_REGISTRY, IMPACT_SOLUTION_FIELD_BY_IX_ID, impactSolutionWriteKey, route, apolloClient, props, sectionResult, loading, section, updateImpact, updateImpactLean, updateAccount, upsertGhgSavingPotential, upsertPromptRun, upsertImpactSolution, resolveValueFromObject, getQuestionValue, resolveComponentName, shouldRenderQuestion, isDisplayOnlyQuestion, isSelfContainedQuestion, isVuetifyCheckboxQuestion, resolveComponentProps, isReadonlyInput, normalizeByRule, handleUpdate, handleAiSuggestion, handleCheckboxChange }
}

})